import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBrandDetail, createBrand, updateBrand } from '../api/brandApi';

export function useBrandEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingBrandId = searchParams.get('id');

  const [form, setForm] = useState({
    name: '',
    description: '',
  });

  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(Boolean(editingBrandId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function loadIfEditing() {
      if (!editingBrandId) return;
      setIsLoading(true);
      setError('');
      try {
        const detail = await getBrandDetail(editingBrandId);
        if (detail) {
          setForm({
            name: detail.name || '',
            description: detail.description || '',
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Brand not found.');
        }
      } catch (e) {
        setError('Failed to load brand details.');
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingBrandId]);

  const patchForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Brand Name is required.');
      return;
    }
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        status: operationalStatus === 'active' ? 'Active' : 'Archive / Deactive'
      };
      if (editingBrandId) {
        await updateBrand(editingBrandId, payload);
        setSuccess('Brand updated successfully.');
      } else {
        await createBrand(payload);
        setSuccess('Brand created successfully.');
      }
      setTimeout(() => {
        navigate('/masters/brand/list');
      }, 1000);
    } catch (e) {
      setError('Failed to save brand.');
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const fields = ['name'];
    const filled = fields.filter(f => form[f]?.trim?.()).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingBrandId),
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    }
  };
}
